import React, { useEffect, useState, useRef, lazy } from 'react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence, useReducedMotion, useInView } from 'framer-motion';
import { ArrowRight, Link2Icon, PencilIcon, CheckIcon, X, Search, Sparkles, Send, Check, TrendingUp, Briefcase, UserPlus, Radio, Wallet, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCopy } from './copy/copyMode';
import { PlatformCycleProvider, usePlatformCycle } from './copy/RotatingPlatform';
import { WaitlistCTA } from './WaitlistCTA';
import { TALK_TO_SALES_URL } from '../lib/links';
// Company names for the "Trusted by fast-moving teams" carousel — same set as
// the future homepage hero (MarketingHero), rendered as text logos.
const TRUST_LOGOS = ['Ember Labs', 'Entropy Studios', 'Tonic', 'Larger Caveman', 'No Contact', 'Halcyon Group'];
// Tool logos for the hero "Giga plugs into your world" carousel — the apps
// Giga connects to, rendered as brand icons (simpleicons, with a graceful
// fallback if a slug is unavailable).
const TRUST_TOOL_LOGOS: {
  slug: string;
  name: string;
  light?: boolean;
}[] = [{
  slug: 'slack',
  name: 'Slack'
}, {
  slug: 'notion',
  name: 'Notion',
  light: true
}, {
  slug: 'salesforce',
  name: 'Salesforce'
}, {
  slug: 'hubspot',
  name: 'HubSpot'
}, {
  slug: 'stripe',
  name: 'Stripe'
}, {
  slug: 'gmail',
  name: 'Gmail'
}, {
  slug: 'googledrive',
  name: 'Google Drive'
}, {
  slug: 'linear',
  name: 'Linear'
}, {
  slug: 'jira',
  name: 'Jira'
}, {
  slug: 'asana',
  name: 'Asana'
}, {
  slug: 'zendesk',
  name: 'Zendesk',
  light: true
}, {
  slug: 'figma',
  name: 'Figma'
}, {
  slug: 'github',
  name: 'GitHub',
  light: true
}, {
  slug: 'googlecalendar',
  name: 'Google Calendar'
}, {
  slug: 'zapier',
  name: 'Zapier'
}, {
  slug: 'airtable',
  name: 'Airtable'
}];
// Reuse the integrations data from IntegrationsSection
const ALL_INTEGRATION_SLUGS: {
  slug: string;
  name: string;
  light?: boolean;
}[] = [{
  slug: 'slack',
  name: 'Slack'
}, {
  slug: 'github',
  name: 'GitHub',
  light: true
}, {
  slug: 'gitlab',
  name: 'GitLab'
}, {
  slug: 'figma',
  name: 'Figma'
}, {
  slug: 'notion',
  name: 'Notion',
  light: true
}, {
  slug: 'linear',
  name: 'Linear'
}, {
  slug: 'hubspot',
  name: 'HubSpot'
}, {
  slug: 'stripe',
  name: 'Stripe'
}, {
  slug: 'salesforce',
  name: 'Salesforce'
}, {
  slug: 'zendesk',
  name: 'Zendesk',
  light: true
}, {
  slug: 'jira',
  name: 'Jira'
}, {
  slug: 'zoom',
  name: 'Zoom'
}, {
  slug: 'googledrive',
  name: 'Google Drive'
}, {
  slug: 'dropbox',
  name: 'Dropbox'
}, {
  slug: 'asana',
  name: 'Asana'
}, {
  slug: 'shopify',
  name: 'Shopify'
}, {
  slug: 'mailchimp',
  name: 'Mailchimp'
}, {
  slug: 'atlassian',
  name: 'Atlassian'
}, {
  slug: 'mondaydotcom',
  name: 'Monday'
}, {
  slug: 'confluence',
  name: 'Confluence',
  light: true
}, {
  slug: 'miro',
  name: 'Miro'
}, {
  slug: 'clickup',
  name: 'ClickUp'
}, {
  slug: 'zapier',
  name: 'Zapier'
}, {
  slug: 'intercom',
  name: 'Intercom'
}, {
  slug: 'airtable',
  name: 'Airtable'
}, {
  slug: 'trello',
  name: 'Trello'
}, {
  slug: 'calendly',
  name: 'Calendly'
}, {
  slug: 'loom',
  name: 'Loom'
}, {
  slug: 'googlecalendar',
  name: 'Google Calendar'
}, {
  slug: 'gmail',
  name: 'Gmail'
}, {
  slug: 'googlemeet',
  name: 'Google Meet'
}, {
  slug: 'googlesheets',
  name: 'Google Sheets'
}, {
  slug: 'googledocs',
  name: 'Google Docs',
  light: true
}, {
  slug: 'microsoftteams',
  name: 'Microsoft Teams'
}, {
  slug: 'microsoftoutlook',
  name: 'Outlook'
}, {
  slug: 'microsoftexcel',
  name: 'Excel'
}, {
  slug: 'pipedrive',
  name: 'Pipedrive'
}, {
  slug: 'segment',
  name: 'Segment'
}, {
  slug: 'amplitude',
  name: 'Amplitude'
}, {
  slug: 'mixpanel',
  name: 'Mixpanel'
}, {
  slug: 'datadog',
  name: 'Datadog'
}, {
  slug: 'sentry',
  name: 'Sentry'
}, {
  slug: 'pagerduty',
  name: 'PagerDuty'
}, {
  slug: 'twilio',
  name: 'Twilio'
}, {
  slug: 'sendgrid',
  name: 'SendGrid'
}, {
  slug: 'quickbooks',
  name: 'QuickBooks'
}, {
  slug: 'xero',
  name: 'Xero'
}, {
  slug: 'docusign',
  name: 'DocuSign'
}, {
  slug: 'webflow',
  name: 'Webflow',
  light: true
}, {
  slug: 'contentful',
  name: 'Contentful'
}, {
  slug: 'wordpress',
  name: 'WordPress'
}, {
  slug: 'discord',
  name: 'Discord'
}, {
  slug: 'gong',
  name: 'Gong'
}, {
  slug: 'snowflake',
  name: 'Snowflake'
}, {
  slug: 'looker',
  name: 'Looker'
}, {
  slug: 'tableau',
  name: 'Tableau'
}, {
  slug: 'box',
  name: 'Box'
}, {
  slug: 'okta',
  name: 'Okta'
}, {
  slug: 'aws',
  name: 'AWS',
  light: true
}, {
  slug: 'googlecloud',
  name: 'Google Cloud'
}, {
  slug: 'microsoftazure',
  name: 'Azure'
}, {
  slug: 'postman',
  name: 'Postman'
}, {
  slug: 'launchdarkly',
  name: 'LaunchDarkly'
}, {
  slug: 'pendo',
  name: 'Pendo'
}];
const MARQUEE_SET: {
  slug: string;
  name: string;
  light?: boolean;
}[] = [{
  slug: 'slack',
  name: 'Slack'
}, {
  slug: 'gmail',
  name: 'Gmail'
}, {
  slug: 'googledrive',
  name: 'Google Drive'
}, {
  slug: 'googlecalendar',
  name: 'Google Calendar'
}, {
  slug: 'hubspot',
  name: 'HubSpot'
}, {
  slug: 'salesforce',
  name: 'Salesforce'
}, {
  slug: 'asana',
  name: 'Asana'
}, {
  slug: 'airtable',
  name: 'Airtable'
}, {
  slug: 'notion',
  name: 'Notion',
  light: true
}, {
  slug: 'linear',
  name: 'Linear'
}, {
  slug: 'calendly',
  name: 'Calendly'
}, {
  slug: 'zoom',
  name: 'Zoom'
}, {
  slug: 'intercom',
  name: 'Intercom'
}, {
  slug: 'dropbox',
  name: 'Dropbox'
}, {
  slug: 'figma',
  name: 'Figma'
}, {
  slug: 'stripe',
  name: 'Stripe'
}];
function LogoTile({
  logo
}: {
  logo: {
    slug: string;
    name: string;
    light?: boolean;
  };
}) {
  return <div className="shrink-0 flex items-center justify-center w-16 h-16 md:w-[100px] md:h-[100px] rounded-xl md:rounded-2xl bg-[#1A1410] border border-white/[0.07] shadow-lg shadow-black/30" title={logo.name} data-id="element-4507" data-mp="3g3h56d1h5i8w">
      <img src={`https://cdn.simpleicons.org/${logo.slug}${logo.light ? '/e8e6e3' : ''}`} alt={logo.name} className="w-7 h-7 md:w-11 md:h-11 object-contain" loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${logo.slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-4508" data-mp="1e7nmlg6077al" />
    </div>;
}
function LogoMarquee({
  reducedMotion
}: {
  reducedMotion: boolean;
}) {
  return <div className="relative w-full overflow-hidden" style={{
    WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%)',
    maskImage: 'linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%)'
  }} data-id="element-4509" data-mp="1qkr91tzm0mpr">
      <motion.div className="flex gap-3 md:gap-5 w-max" animate={reducedMotion ? undefined : {
      x: ['-50%', '0%']
    }} transition={reducedMotion ? undefined : {
      duration: 40,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-4510" data-mp="2ea3fwn7gsiow">
        {[...MARQUEE_SET, ...MARQUEE_SET].map((logo, i) => <LogoTile key={`${logo.slug}-${i}`} logo={logo} data-id="element-4511" data-mp="2lskv9a20urps" data-mpi={i} />)}
      </motion.div>
    </div>;
}
// Trusted-by company names. The page already has plenty of motion, so this
// beat stays still — no scrolling marquee. At every breakpoint the names sit on
// a single static line, centered, inside an overflow-hidden track with soft
// left/right fade masks — reading like a (non-moving) carousel where the outer
// names are allowed to clip behind the fade. Gaps widen from mobile up so the
// row fills the container and the fades read cleanly on desktop too.
// (The `reducedMotion` prop is kept for call-site compatibility.)
function TrustLogoMarquee({
  reducedMotion: _reducedMotion,
  isHero
}: {
  reducedMotion: boolean;
  isHero: boolean;
}) {
  const nameClass = `whitespace-nowrap font-title text-sm font-medium tracking-tight ${isHero ? 'text-[#2B211A]/45' : 'text-white/60'}`;
  // Only the trailing edge fades. With the label sitting to the left of the
  // row, a symmetric mask would dim the first name right where the eye enters.
  return <div className="flex min-w-0 flex-1 items-center justify-center gap-x-6 overflow-hidden sm:gap-x-9 md:justify-start md:gap-x-11" style={{
    WebkitMaskImage: 'linear-gradient(to right, #000 88%, transparent 100%)',
    maskImage: 'linear-gradient(to right, #000 88%, transparent 100%)'
  }} data-id="element-4512" data-mp="8me52w8mn3dw">
      {TRUST_LOGOS.map((name, i) => <span key={`${name}-${i}`} className={nameClass} data-id="element-4513" data-mp="3m1k81upx3jx0" data-mpi={i}>
          {name}
        </span>)}
    </div>;
}
const STEPS = [{
  n: 1,
  icon: Link2Icon,
  title: 'Connect your tools',
  body: 'Get set up in minutes — no APIs to wrangle, no engineers required.'
}, {
  n: 2,
  icon: PencilIcon,
  title: 'Describe the task',
  body: 'Say what you want in plain English — the outcome, not the steps.'
}, {
  n: 3,
  icon: CheckIcon,
  title: 'Giga gets it done',
  body: "Giga picks the tools, does the work, and pings you the moment it's done."
}];
type PromptSegment = {
  type: 'text';
  content: string;
} | {
  type: 'chip';
  slug: string;
  name: string;
  light?: boolean;
} | {
  type: 'mention';
  name: string;
};
type PromptAction = {
  slug: string;
  name: string;
  label: string;
};
type Workflow = {
  segments: PromptSegment[];
  actions: PromptAction[];
};
// Different workflows the section rotates through, so it's clear Giga handles
// many use cases — not just one scripted demo.
const WORKFLOWS: Workflow[] = [
// Everyday jobs a business owner instantly recognizes — getting paid,
// booking clients, the weekly sales recap, and keeping stock topped up —
// using the tools they already run their business on.
{
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: ' send this week\u2019s invoices from '
  }, {
    type: 'chip',
    slug: 'quickbooks',
    name: 'QuickBooks'
  }, {
    type: 'text',
    content: ' chase the overdue ones in '
  }, {
    type: 'chip',
    slug: 'gmail',
    name: 'Gmail'
  }, {
    type: 'text',
    content: ' and log every payment in '
  }, {
    type: 'chip',
    slug: 'googlesheets',
    name: 'Google Sheets'
  }],
  actions: [{
    slug: 'quickbooks',
    name: 'QuickBooks',
    label: 'Sent this week\u2019s invoices from QuickBooks'
  }, {
    slug: 'gmail',
    name: 'Gmail',
    label: 'Emailed reminders for the overdue invoices'
  }, {
    slug: 'googlesheets',
    name: 'Google Sheets',
    label: 'Logged each payment in Google Sheets'
  }]
}, {
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: ' pull this week\u2019s new bookings from '
  }, {
    type: 'chip',
    slug: 'calendly',
    name: 'Calendly'
  }, {
    type: 'text',
    content: ' send each client a confirmation in '
  }, {
    type: 'chip',
    slug: 'gmail',
    name: 'Gmail'
  }, {
    type: 'text',
    content: ' and add them to my list in '
  }, {
    type: 'chip',
    slug: 'mailchimp',
    name: 'Mailchimp'
  }],
  actions: [{
    slug: 'calendly',
    name: 'Calendly',
    label: 'Pulled this week\u2019s new bookings from Calendly'
  }, {
    slug: 'gmail',
    name: 'Gmail',
    label: 'Sent each client a confirmation in Gmail'
  }, {
    slug: 'mailchimp',
    name: 'Mailchimp',
    label: 'Added them to your Mailchimp list'
  }]
}, {
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: ' total up this week\u2019s sales from '
  }, {
    type: 'chip',
    slug: 'stripe',
    name: 'Stripe'
  }, {
    type: 'text',
    content: ' build the summary in '
  }, {
    type: 'chip',
    slug: 'googlesheets',
    name: 'Google Sheets'
  }, {
    type: 'text',
    content: ' and post the recap to my team in '
  }, {
    type: 'chip',
    slug: 'slack',
    name: 'Slack'
  }],
  actions: [{
    slug: 'stripe',
    name: 'Stripe',
    label: 'Totaled this week\u2019s sales from Stripe'
  }, {
    slug: 'googlesheets',
    name: 'Google Sheets',
    label: 'Built the sales summary in Google Sheets'
  }, {
    slug: 'slack',
    name: 'Slack',
    label: 'Posted the recap to your team in Slack'
  }]
}, {
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: ' check what\u2019s low on stock in '
  }, {
    type: 'chip',
    slug: 'shopify',
    name: 'Shopify'
  }, {
    type: 'text',
    content: ' email my supplier a reorder in '
  }, {
    type: 'chip',
    slug: 'gmail',
    name: 'Gmail'
  }, {
    type: 'text',
    content: ' and update the count in '
  }, {
    type: 'chip',
    slug: 'googlesheets',
    name: 'Google Sheets'
  }],
  actions: [{
    slug: 'shopify',
    name: 'Shopify',
    label: 'Found the low-stock items in Shopify'
  }, {
    slug: 'gmail',
    name: 'Gmail',
    label: 'Emailed your supplier a reorder from Gmail'
  }, {
    slug: 'googlesheets',
    name: 'Google Sheets',
    label: 'Updated the stock count in Google Sheets'
  }]
},
// Prospecting / lead-gen — find new leads, enrich them into the CRM, and
// kick off outreach, using the tools sales teams live in.
{
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: ' find new leads that fit my ICP on '
  }, {
    type: 'chip',
    slug: 'linkedin',
    name: 'LinkedIn'
  }, {
    type: 'text',
    content: ' add them to my pipeline in '
  }, {
    type: 'chip',
    slug: 'hubspot',
    name: 'HubSpot'
  }, {
    type: 'text',
    content: ' and send each a first-touch email from '
  }, {
    type: 'chip',
    slug: 'gmail',
    name: 'Gmail'
  }],
  actions: [{
    slug: 'linkedin',
    name: 'LinkedIn',
    label: 'Found new leads matching your ICP on LinkedIn'
  }, {
    slug: 'hubspot',
    name: 'HubSpot',
    label: 'Added them to your pipeline in HubSpot'
  }, {
    slug: 'gmail',
    name: 'Gmail',
    label: 'Sent each a first-touch email from Gmail'
  }]
},
// Recruiting — source candidates, screen the inbound applicants, and book
// the promising ones onto the calendar.
{
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: ' source candidates for my open role on '
  }, {
    type: 'chip',
    slug: 'linkedin',
    name: 'LinkedIn'
  }, {
    type: 'text',
    content: ' screen the applicants in '
  }, {
    type: 'chip',
    slug: 'gmail',
    name: 'Gmail'
  }, {
    type: 'text',
    content: ' and book the top ones on '
  }, {
    type: 'chip',
    slug: 'googlecalendar',
    name: 'Google Calendar'
  }],
  actions: [{
    slug: 'linkedin',
    name: 'LinkedIn',
    label: 'Sourced candidates for the role on LinkedIn'
  }, {
    slug: 'gmail',
    name: 'Gmail',
    label: 'Screened the inbound applicants in Gmail'
  }, {
    slug: 'googlecalendar',
    name: 'Google Calendar',
    label: 'Booked the top candidates on Google Calendar'
  }]
}];
function ToolChip({
  slug,
  name,
  light
}: {
  slug: string;
  name: string;
  light?: boolean;
}) {
  return <motion.span initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.2,
    ease: 'easeOut'
  }} className="relative mr-1 -my-0.5 inline-flex items-center gap-1 md:gap-1.5 pl-1.5 pr-2 md:pl-2 md:pr-2.5 py-[1px] md:py-0.5 rounded-md md:rounded-lg bg-[#F4F0EA] border border-neutral-200 text-[13px] md:text-sm font-medium leading-none text-[#2B211A] align-middle shadow-sm overflow-hidden" data-id="element-4514" data-mp="42mwrrnlmw01">
      <img src={`https://cdn.simpleicons.org/${slug}`} alt={name} className="relative w-3 h-3 md:w-3.5 md:h-3.5 object-contain" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-4515" data-mp="15t4m5nm7awsy" />

      <span className="relative" data-id="element-4516" data-mp="2qa15usmboolq">{name}</span>
    </motion.span>;
}
// Slack-style @mention token for Giga — renders like a clickable user mention
// inside the message (highlighted pill with an @ prefix).
function GigaMention({
  name
}: {
  name: string;
}) {
  return <motion.span initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.2,
    ease: 'easeOut'
  }} role="link" className="inline rounded-[3px] pl-1 pr-0.5 text-[#EA580C] font-semibold cursor-pointer transition-colors hover:underline bg-[#EA580C]/12" data-id="element-4517" data-mp="1nth10hag509c">
      @{name}
    </motion.span>;
}
// Total "type units" in a prompt: every text character counts as one unit and
// every chip counts as one unit, so we can reveal the message progressively.
function promptUnitCount(segments: PromptSegment[]) {
  return segments.reduce((sum, seg) => sum + (seg.type === 'text' ? seg.content.length : 1), 0);
}
// Render the portion of a prompt that has been "typed" so far. Text reveals
// character-by-character; chips pop in as a whole the moment they're reached.
function renderTyped(segments: PromptSegment[], revealed: number): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let cursor = 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (seg.type === 'text') {
      const shown = Math.max(0, Math.min(seg.content.length, revealed - cursor));
      if (shown > 0) {
        out.push(<span key={i} data-id="element-4518" data-mp="3w1c713v7u7pl">{seg.content.slice(0, shown)}</span>);
      }
      cursor += seg.content.length;
    } else if (seg.type === 'mention') {
      if (revealed > cursor) {
        out.push(<GigaMention key={i} name={seg.name} data-id="element-4519" data-mp="3mc2h9unuh14n" />);
      }
      cursor += 1;
    } else {
      if (revealed > cursor) {
        out.push(<ToolChip key={i} slug={seg.slug} name={seg.name} light={seg.light} data-id="element-4520" data-mp="3heakam8ceotl" />);
      }
      cursor += 1;
    }
    if (cursor >= revealed) break;
  }
  return out;
}
const TABS = [{
  label: 'Sales & Leads',
  index: 4,
  icon: TrendingUp
}, {
  label: 'Finance & Ops',
  index: 0,
  icon: Briefcase
}, {
  label: 'Recruiting',
  index: 5,
  icon: UserPlus
}, {
  label: 'Anything else...',
  index: 1,
  icon: Sparkles
}];
function TypewriterInputCard({
  reducedMotion,
  isHeroTone = true
}: {
  reducedMotion: boolean;
  /** Light (cream hero) vs dark section, which the tab row's rule follows. */
  isHeroTone?: boolean;
}) {
  const [workflowIndex, setWorkflowIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  // The card dresses itself as whichever surface the headline is naming, so
  // "connects your tools to ChatGPT / Claude / Slack" is demonstrated rather
  // than only asserted.
  const cycle = usePlatformCycle();
  const composer = cycle?.platform.composer;
  const workflow = WORKFLOWS[workflowIndex];
  const totalUnits = promptUnitCount(workflow.segments);
  // Reveal the prompt one "type unit" at a time, then pause and cycle to the
  // next workflow so visitors see multiple use cases — no send/action loop.
  const [revealed, setRevealed] = useState(0);
  // Type faster on mobile, where the slower cadence felt sluggish.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef);
  useEffect(() => {
    if (reducedMotion) {
      setRevealed(totalUnits);
      return;
    }
    if (!inView) return;
    if (revealed < totalUnits) {
      const atChip = (() => {
        let cursor = 0;
        for (const seg of workflow.segments) {
          const len = seg.type === 'text' ? seg.content.length : 1;
          if (revealed >= cursor && revealed < cursor + len) {
            return seg.type === 'chip';
          }
          cursor += len;
        }
        return false;
      })();
      const charMs = isMobile ? 11 : 22;
      const chipMs = isMobile ? 80 : 140;
      const id = setTimeout(() => setRevealed(r => r + 1), atChip ? chipMs : charMs);
      return () => clearTimeout(id);
    } else {
      // Fully typed — hold for a beat, then advance to the next workflow.
      if (isAutoPlaying) {
        const id = setTimeout(() => {
          setWorkflowIndex(i => {
            // Find the next tab index to cycle through the tabs
            const currentTabIndex = TABS.findIndex(t => t.index === i);
            if (currentTabIndex !== -1) {
              return TABS[(currentTabIndex + 1) % TABS.length].index;
            }
            return (i + 1) % WORKFLOWS.length;
          });
          setRevealed(0);
        }, 1800);
        return () => clearTimeout(id);
      }
    }
  }, [reducedMotion, inView, revealed, totalUnits, workflow.segments, workflowIndex, isMobile, isAutoPlaying]);
  const isTyping = !reducedMotion && revealed < totalUnits;
  const handleTabClick = (index: number) => {
    setIsAutoPlaying(false);
    setWorkflowIndex(index);
    setRevealed(0);
  };
  return <div className="flex w-full flex-col items-center gap-5" data-id="element-4521" data-mp="1xzpqa21zz45f">
      {/* Prompt categories, above the composer and styled as underlined text
       tabs rather than pills. Sitting below the card they read as a second
       row of buttons competing with the CTA pair beneath; here they read as
       what they are — a filter on the prompt being typed. */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.4,
      delay: 0.1
    }} className="mx-auto w-full max-w-[820px] overflow-x-auto" data-id="element-4522" data-mp="2cxql0sr539xz">
        <div className={`flex min-w-max items-end justify-center gap-6 border-b md:gap-9 ${isHeroTone ? 'border-[#2B211A]/10' : 'border-white/15'}`} data-id="element-4523" data-mp="27x2sg2n9il1n">
          {TABS.map(tab => {
          const isActive = workflowIndex === tab.index;
          const Icon = tab.icon;
          return <button key={tab.label} onClick={() => handleTabClick(tab.index)} aria-pressed={isActive} className={cn('relative flex items-center gap-2 pb-3 text-[13px] font-semibold transition-colors duration-200 md:text-sm', isActive ? 'text-[#EA580C]' : isHeroTone ? 'text-neutral-500 hover:text-neutral-800' : 'text-white/55 hover:text-white/85')} data-id="element-4524" data-mp="cgmbrnhaj70h">
                {/* Icon only on the active tab. Four glyphs at once put more
                 marks in the hero than the labels needed. */}
                {isActive && <Icon size={15} strokeWidth={1.9} data-id="element-4525" data-mp="3kw60qaaxi39q" />}
                {tab.label}
                {isActive && <motion.span layoutId="prompt-tab-underline" className="absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-[#EA580C]" transition={{
              duration: 0.25,
              ease: [0.16, 1, 0.3, 1]
            }} data-id="element-4526" data-mp="3gukgg3w2empi" />}
              </button>;
        })}
        </div>
      </motion.div>

      <motion.div ref={containerRef} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.6,
      delay: 0
    }} style={{
      backgroundColor: composer?.surface ?? '#FFFFFF',
      borderColor: composer?.border ?? 'rgba(229,229,229,0.8)'
    }}
    // The surface is tinted with a plain CSS transition rather than a framer
    // `animate` prop: the card already uses `whileInView` for its entrance,
    // and a second animation target on the same element fights it.
    // A soft warm shadow rather than shadow-2xl: the card only needs to lift
    // off the cream, and the heavy black-tinted drop was reading as grime
    // against a warm background.
    className="relative rounded-[24px] border shadow-[0_18px_50px_-20px_rgba(43,33,26,0.18)] p-5 pb-10 md:p-8 md:pb-9 max-w-[820px] w-full mx-auto transition-[background-color,border-color] duration-300 ease-out" data-id="element-4527" data-mp="1a4368zaan18e">
        {/* Fixed-height text area so the card never grows/shrinks as the prompt
                                                                                                                                                                                                                                                types in or swaps workflows. The first lines run full width; only the
                                                                                                                                                                                                                                                bottom-right corner (where the send button sits) is carved out via a
                                                                                                                                                                                                                                                floated shape-outside spacer, so the last line wraps before the arrow
                                                                                                                                                                                                                                                circle instead of sliding under it. */}
        <div className="text-left text-sm md:text-xl font-medium text-[#2B211A] leading-[1.95] md:leading-[1.85] h-[132px] md:h-[112px] overflow-hidden" data-id="element-4528" data-mp="3tyy6x9yzf85h">
          {/* Invisible spacer floated bottom-right: reserves just enough room for
           the send button so the LAST TWO lines (where the button sits) clear
           it, while the top lines run the card's full width with equal L/R
           padding. Width is kept tight so text only steps back as far as the
           button needs — never further. */}
          <span aria-hidden="true" className="float-right h-full w-[3rem] md:w-[4rem]" style={{
          shapeOutside: 'inset(calc(100% - 5rem) 0 0 0)'
        }} data-id="element-4529" data-mp="3fak5n3h7rrr1" />

          {renderTyped(workflow.segments, revealed)}
          {isTyping && <span aria-hidden="true" className="inline-block w-[2px] h-[1em] align-middle ml-0.5 animate-pulse" style={{
          backgroundColor: composer?.caret ?? '#EA580C'
        }} data-id="element-4530" data-mp="3682f4cuj0cy8" />}
        </div>

        <PromptSendButton data-id="element-4531" data-mp="16zsrjzqvuf8m" />
      </motion.div>
    </div>;
}
// Send button for the prompt card. It mirrors the SAME platform the headline
// pill is currently showing (shared via usePlatformCycle): Slack → solid green
// square-ish button, Teams → solid purple circular button, each with the real
// paper-plane glyph — so it changes in lockstep as the pill flips Slack ↔
// Teams. Falls back to the Giga-orange round button if no platform cycle is
// mounted above (e.g. reduced motion or standalone use).
function PromptSendButton() {
  const cycle = usePlatformCycle();
  const send = cycle?.platform.send;
  const isRound = send ? send.shape === 'round' : true;
  const bg = send ? send.color : '#EA580C';
  return <motion.div key={cycle ? cycle.platform.name : 'default'} initial={{
    opacity: 0,
    scale: 0.8
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.35,
    ease: [0.16, 1, 0.3, 1]
  }} className={`absolute bottom-5 right-5 md:bottom-8 md:right-8 w-10 h-10 md:w-12 md:h-12 shrink-0 text-white flex items-center justify-center shadow-lg ${isRound ? 'rounded-full' : 'rounded-xl'}`} style={{
    backgroundColor: bg
  }} data-id="element-4532" data-mp="a9a2mt0qd0e4">
      {/* Paper-plane glyph — matches the real Slack/Teams composer send icon. */}
      <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] md:w-5 md:h-5 -ml-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-4533" data-mp="dzvzb5mls6w3">
        <path d="m22 2-7 20-4-9-9-4Z" data-id="element-4534" data-mp="nx0rbm34qwkt" />
        <path d="M22 2 11 13" data-id="element-4535" data-mp="ny1znisrh4yu" />
      </svg>
    </motion.div>;
}
// What "Giga gets it done" actually produces: a notification-style result card
// showing the finished output, so the section's "action out" promise is concrete
// instead of abstract. Mirrors the second workflow (Zoom → Linear → Gmail).
const RESULT_ACTIONS: {
  slug: string;
  light?: boolean;
  label: string;
}[] = [{
  slug: 'quickbooks',
  label: 'Sent this week\u2019s invoices from QuickBooks'
}, {
  slug: 'gmail',
  label: 'Emailed reminders for the overdue invoices'
}, {
  slug: 'googlesheets',
  label: 'Logged each payment in Google Sheets'
}];
function ResultActionIcon({
  slug,
  light
}: {
  slug: string;
  light?: boolean;
}) {
  return <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-white shrink-0" data-id="element-4536" data-mp="158ywz2ym82km">
      <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt="" aria-hidden="true" className="w-4 h-4 object-contain" loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.simpleicons.org/${slug}${light ? '/2B211A' : ''}`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-4537" data-mp="hj4o0whf2y5n" />
    </span>;
}
// Single tool lockup (icon + name) for the hero "Giga plugs into your world"
// carousel. Icons are rendered MONOCHROME — every logo is forced to one solid
// tone (dark on the light hero, light on dark) so the row reads clean instead
// of a rainbow of brand colors.
function TrustToolLogo({
  slug,
  name,
  light,
  isHero
}: {
  slug: string;
  name: string;
  light?: boolean;
  isHero?: boolean;
}) {
  // Force a single fill color via simpleicons' /HEX param for true monochrome.
  const tone = isHero ? '2B211A' : 'e8e6e3';
  return <div className="flex items-center gap-2.5 shrink-0" data-id="element-4538" data-mp="2t4dsdct44z2c">
      <img src={`https://cdn.simpleicons.org/${slug}/${tone}`} alt="" aria-hidden="true" loading="lazy" className="h-6 w-6 object-contain opacity-80" onError={e => {
      ;
      (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
    }} data-id="element-4539" data-mp="2t3ub7ybaeepd" />

      <span className={`text-sm font-semibold whitespace-nowrap ${isHero ? 'text-neutral-600' : 'text-white/80'}`} data-id="element-4540" data-mp="373mx9gbvc6r0">
        {name}
      </span>
    </div>;
}
function ResultNotification({
  reducedMotion
}: {
  reducedMotion: boolean;
}) {
  return <div className="mt-16 md:mt-24" data-id="element-4541" data-mp="hoylyt7tefge">
      {/* Small lead-in connecting the steps to the payoff */}
      <motion.p initial={reducedMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.5
    }} className="text-xs font-semibold tracking-[4px] uppercase text-neutral-400 mb-6" data-id="element-4542" data-mp="mn06likw2mtz">
        And here's what shows up done
      </motion.p>

      <motion.div initial={reducedMotion ? {
      opacity: 1,
      y: 0,
      scale: 1
    } : {
      opacity: 0,
      y: 24,
      scale: 0.98
    }} whileInView={{
      opacity: 1,
      y: 0,
      scale: 1
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      type: 'spring',
      stiffness: 220,
      damping: 24
    }} className="relative text-left max-w-[520px] mx-auto rounded-[22px] bg-white/[0.06] backdrop-blur-sm border border-white/10 shadow-2xl shadow-black/40 overflow-hidden" data-id="element-4543" data-mp="3pyxpmo7s2kr">
        {/* Header — looks like a real notification from Giga */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10" data-id="element-4544" data-mp="34msv7wyzxoiz">
          <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#EA580C] shrink-0" data-id="element-4545" data-mp="2ztq03kekm70o">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-4 h-4 object-contain [filter:brightness(0)_invert(1)]" data-id="element-4546" data-mp="2uvxy1xk7p4ir" />
          </span>
          <div className="min-w-0" data-id="element-4547" data-mp="2gxhv8hpgzy5w">
            <p className="text-sm font-semibold text-white leading-tight" data-id="element-4548" data-mp="2gyj6am6hkknm">
              Giga
            </p>
            <p className="text-[11px] text-neutral-400 leading-tight" data-id="element-4549" data-mp="2bzokrch4whzt">
              Co-pilot · just now
            </p>
          </div>
          <span className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#EA580C]/15 border border-[#EA580C]/30 text-[11px] font-bold text-[#F8B98C] uppercase tracking-wide shrink-0" data-id="element-4550" data-mp="a5pex6ms6sui">
            <Check size={12} strokeWidth={3} data-id="element-4551" data-mp="29dz2hjqfcqk4" />
            Done
          </span>
        </div>

        {/* Body */}
        <div className="px-5 py-4" data-id="element-4552" data-mp="1upcg4y2dqyje">
          <p className="text-sm text-neutral-200 leading-relaxed mb-4" data-id="element-4553" data-mp="1um6nx9ygfgjg">
            <span className="font-semibold text-white" data-id="element-4554" data-mp="1zndw3a4iehqn">Done.</span> Here's what
            I took off your plate — ready for you to review:
          </p>

          <ul className="flex flex-col gap-2.5" data-id="element-4555" data-mp="2scbd79wzi8ht">
            {RESULT_ACTIONS.map((action, i) => <motion.li key={action.slug} initial={reducedMotion ? {
            opacity: 1,
            x: 0
          } : {
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: reducedMotion ? 0 : 0.25 + i * 0.18
          }} className="flex items-center gap-3" data-id="element-4556" data-mp="2dercwgb191hz" data-mpi={i}>
                <ResultActionIcon slug={action.slug} light={action.light} data-id="element-4557" data-mp="3l8aqzvpmsdgs" />
                <span className="flex-1 text-sm text-neutral-300 leading-snug" data-id="element-4558" data-mp="3g6lescqo9tr4">
                  {action.label}
                </span>
                <motion.span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C] text-white shrink-0" initial={reducedMotion ? {
              scale: 1
            } : {
              scale: 0
            }} whileInView={{
              scale: 1
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 18,
              delay: reducedMotion ? 0 : 0.4 + i * 0.18
            }} data-id="element-4559" data-mp="3q5b0kglovycn">
                  <Check size={12} strokeWidth={3} data-id="element-4560" data-mp="2iqo5tm3xqb4b" />
                </motion.span>
              </motion.li>)}
          </ul>
        </div>
      </motion.div>
    </div>;
}
export function HowItWorksConnections({
  mode = 'full',
  transparentBg = false,
  headline
}: {
  // 'full'  → everything (hero chat + steps + connections) — used at /future
  // 'hero'  → just the "Ask Giga anything" headline + chat composer
  // 'how'   → the "How it works" 3-step explainer + result + connections
  mode?: 'full' | 'hero' | 'how'; // When true (hero mode only), the section drops its own opaque cream fill +
  // dot-matrix texture so a backdrop placed BEHIND it (e.g. the experimental
  // Base44 grid on /grid-hero) shows through. Default keeps the normal look.
  transparentBg?: boolean; // Route-specific copy for experimental hero directions. Production callers
  // continue to use the shared copy registry by default.
  headline?: React.ReactNode;
}) {
  const copy = useCopy();
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const isHero = mode === 'hero';
  const showHero = mode === 'hero' || mode === 'full';
  const showSteps = mode === 'how' || mode === 'full';
  const showConnections = mode === 'how' || mode === 'full';
  return (
    // Shared platform cycle: the headline pill (RotatingPlatform) and the
    // prompt card's send button both read from this, so they flip Slack ↔
    // Teams in perfect lockstep.
    <PlatformCycleProvider data-id="element-4561" data-mp="1ddfl6e525yh9">
      <section className={`relative w-full overflow-hidden ${isHero ? `${transparentBg ? 'bg-transparent' : 'bg-[#FAF8F4]'} text-[#2B211A] pt-32 pb-10 md:pt-44 md:pb-12` : 'bg-[#2B211A] text-white py-16 md:py-40'}`} data-id="element-4562" data-mp="18h758yj1e191">
        {/* Image background with warm radial glows (only for dark mode) */}
        {!isHero && <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `url('https://cdn.magicpatterns.com/uploads/nrRqqKEDJg9H9wijnoz2Si/How_it_works_(4).png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }} data-id="element-4563" data-mp="1w5gobsov8awh" />}

        {/* Light mode background elements */}
        {/* One texture, not two. The dot matrix and the grain overlay were
         stacked on the same cream field, and together they put visible
         activity behind every word in the hero. */}
        {isHero && !transparentBg && <div className="absolute inset-0 dot-matrix opacity-[0.12] pointer-events-none z-0" style={{
          // Fades out behind the copy so the type sits on plain cream and the
          // texture only reads at the edges of the frame.
          WebkitMaskImage: 'radial-gradient(115% 85% at 50% 45%, transparent 30%, #000 100%)',
          maskImage: 'radial-gradient(115% 85% at 50% 45%, transparent 30%, #000 100%)'
        }} data-id="element-4564" data-mp="2tyzjvodf5as5" />}

        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-4565" data-mp="2b7gfpe7nrqoj">
          {/* HEADER */}
          <div className={`text-center ${isHero ? '' : 'mb-16 md:mb-20'}`} data-id="element-4566" data-mp="21b3nylvobmwf">
            {/* Hero headline + chat composer */}
            {/* The hero headline runs wider than the old 4xl measure and steps
             down from 72px to 64px at the top end, so the claim holds one line
             inside the page container instead of breaking mid-sentence. */}
            {showHero && <h2 className={cn(`font-title font-medium tracking-tight mx-auto mt-0 ${isHero ? 'max-w-[1180px] leading-[1.12] tracking-[-0.02em] text-[#2B211A] text-4xl sm:text-5xl md:text-6xl lg:text-[64px] mb-6 md:mb-8' : 'max-w-4xl leading-[1.05] text-white text-3xl md:text-5xl mb-12 md:mb-16'}`)} data-id="element-4567" data-mp="1iow6nx4wderf">
                {headline ?? copy.howItWorksConnections.headline1}
              </h2>}
            {showHero && !headline && copy.howItWorksConnections.subhead1 && <p className={`mb-12 md:mb-16 max-w-[46ch] mx-auto text-base md:text-lg leading-[1.65] ${isHero ? 'text-neutral-500' : 'text-neutral-300'}`} data-id="element-4568" data-mp="2fdbyjzvhc07p">
                {copy.howItWorksConnections.subhead1}
              </p>}
            {showHero && <div data-id="element-4569" data-mp="2pbhwk6e60m7a">
                <TypewriterInputCard reducedMotion={!!reducedMotion} isHeroTone={isHero} data-id="element-4570" data-mp="2p8vtok27yuve" />

                {/* CTAs — self-serve stays primary; "Book a demo" sits beside
                 it as an outlined secondary for buyers who want to be walked
                 through it before connecting a workspace. Stacks on mobile so
                 neither button gets squeezed. */}
                <div className="mt-10 md:mt-12 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4" data-id="element-4571" data-mp="1eys9nbd2abwd">
                  <WaitlistCTA className="group inline-flex w-full sm:w-auto items-center justify-center gap-2.5 px-7 py-3.5 rounded-full bg-[#EA580C] hover:bg-[#d24e09] text-white font-semibold text-base transition-colors" data-id="element-4572" data-mp="1juqy0yye3tws">
                    Get started for free
                    <ArrowRight size={18} strokeWidth={2.5} className="transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-4573" data-mp="w5y3n6xf1u4a" />
                  </WaitlistCTA>
                  <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className={`inline-flex w-full sm:w-auto items-center justify-center px-7 py-3.5 rounded-full border font-semibold text-base transition-colors ${isHero ? 'border-[#2B211A]/15 text-[#2B211A] hover:border-[#2B211A]/30 hover:bg-white/70' : 'border-white/25 text-white hover:border-white/50 hover:bg-white/10'}`} data-id="element-4574" data-mp="3hsgyemsxv1t">
                    Book a demo
                  </a>
                </div>

                {/* The offer sits directly under the buttons, where it answers
                 the question someone asks the moment they reach for the CTA —
                 rather than above the headline, where it would spend the first
                 thing the eye lands on explaining pricing. */}
                <p className={`mt-4 text-[13px] ${isHero ? 'text-neutral-400' : 'text-white/60'}`} data-id="element-4575" data-mp="1u0iitvh1wota">
                  $100 in credits free · No card required
                </p>

                {/* Social proof as a single row — label left, names right —
                 so it lands within the first viewport instead of costing a
                 stacked label plus a second line below the fold. Set well
                 below the CTA and at low contrast: it is the quietest thing
                 in the hero, and reading as such is what keeps the section
                 feeling composed rather than crowded. */}
                <div className="mt-14 md:mt-20 mx-auto flex w-full max-w-3xl flex-col items-center gap-3 md:flex-row md:items-center md:gap-7" data-id="element-4576" data-mp="c1nwxhn00huc">
                  <p className={`shrink-0 text-[10px] font-semibold tracking-[3px] uppercase ${isHero ? 'text-neutral-400/80' : 'text-neutral-500'}`} data-id="element-4577" data-mp="6zghgi2ymjn9">
                    Trusted By
                  </p>
                  <TrustLogoMarquee reducedMotion={!!reducedMotion} isHero={isHero} data-id="element-4578" data-mp="zodxsfnm26dd" />
                </div>
              </div>}

            {/* "How it works" explainer headline (only when the steps lead this
                section, i.e. it is not also the chat hero) */}
            {showSteps && !showHero && <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight text-white leading-[1.1] mb-12 md:mb-16 max-w-4xl mx-auto" data-id="element-4579" data-mp="3ff04gzd1f5tv">
                Connect, describe, done.
              </h2>}

            {/* 3-Step Stepper — sequenced left→right, the connecting line glows
                as it travels and each step lights up the moment it's reached. */}
            {showSteps && <div className="relative" data-id="element-4580" data-mp="1zx2eoookpftl">
                {/* Desktop connecting line */}
                <div className="hidden md:block absolute top-[21px] left-[calc(16.66%-16px)] w-[calc(66.66%+32px)] h-[1px] z-0" data-id="element-4581" data-mp="24qong5rm8zk4">
                  {/* Static dashed base */}
                  <div className="absolute inset-0 border-t border-dashed border-white/25" data-id="element-4582" data-mp="2em8fui6z1g44" />
                  {/* Glowing line that travels left → right */}
                  <motion.div aria-hidden="true" className="absolute inset-y-0 left-0 origin-left" style={{
                  width: '100%'
                }} initial={reducedMotion ? {
                  scaleX: 1
                } : {
                  scaleX: 0
                }} whileInView={{
                  scaleX: reducedMotion ? 1 : [0, 0.5, 0.5, 1]
                }} viewport={{
                  once: true,
                  margin: '-120px'
                }} transition={{
                  duration: reducedMotion ? 0 : 2.2,
                  times: [0, 0.4, 0.6, 1],
                  ease: ['easeInOut', 'linear', 'easeInOut']
                }} data-id="element-4583" data-mp="2nnzhnbye52sp">
                    <div className="absolute inset-0 h-[2px] -mt-px bg-[#EA580C] shadow-[0_0_12px_2px_rgba(234,88,12,0.6)]" data-id="element-4584" data-mp="20l4nq9fcma9s" />
                  </motion.div>
                </div>

                {/* Steps stack vertically on mobile and lay out as a 3-up grid on
                 desktop (with the glowing connecting line). */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-y-12 md:gap-y-0 md:gap-x-12 lg:gap-x-20 relative z-10" data-id="element-4585" data-mp="2kaf47pmx4z8y">
                  {STEPS.map((step, i) => {
                  // The glow front sweeps across over 2.2s, pausing at the middle step.
                  // Light up each step the moment the line reaches it.
                  const lightDelay = reducedMotion ? 0 : i === 0 ? 0.15 : i === 1 ? 0.88 : 2.2;
                  const isLast = i === STEPS.length - 1;
                  return <div key={step.n} className="relative flex flex-col items-center" data-id="element-4586" data-mp="3htngcqr6nign" data-mpi={i}>
                        {/* Badge — pops in like a bubble as the line reaches it */}
                        <motion.div className="relative mb-5 md:mb-6 z-10" initial={reducedMotion ? {
                      opacity: 1,
                      scale: 1
                    } : {
                      opacity: 0,
                      scale: 0.5
                    }} whileInView={{
                      opacity: 1,
                      scale: 1
                    }} viewport={{
                      once: true,
                      margin: '-100px'
                    }} transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 20,
                      delay: lightDelay
                    }} data-id="element-4587" data-mp="37w8zwr59etoc">
                          {/* Bloom of light that fires when the line arrives */}
                          <motion.div aria-hidden="true" className="absolute inset-0 rounded-full bg-[#EA580C] blur-xl" initial={reducedMotion ? {
                        opacity: 0.25,
                        scale: 1.3
                      } : {
                        opacity: 0,
                        scale: 0.6
                      }} whileInView={{
                        opacity: [0, 0.55, 0.25],
                        scale: [0.6, 1.6, 1.3]
                      }} viewport={{
                        once: true,
                        margin: '-100px'
                      }} transition={{
                        duration: 0.8,
                        delay: lightDelay,
                        ease: 'easeOut'
                      }} data-id="element-4588" data-mp="236lmx4wr4dxx" />

                          {/* Dark circular badge with orange border and step number */}
                          <div className="relative w-10 h-10 md:w-11 md:h-11 shrink-0 rounded-full border border-[#EA580C] bg-[#2B211A] flex items-center justify-center" data-id="element-4589" data-mp="34u4gqj80ophy">
                            <span className="font-title text-base md:text-lg font-normal text-[#EA580C] leading-none" data-id="element-4590" data-mp="3hdi8k80zrszv">
                              {step.n}
                            </span>
                          </div>
                        </motion.div>

                        {/* Mobile connector — drops straight down from this step's
                         numbered circle to its OWN headline (circle → headline),
                         so each step reads as a self-contained "1 → its title".
                         Hidden on desktop, which uses the horizontal line above. */}
                        <div className="md:hidden relative h-7 w-[1px] -mt-3 mb-1 z-0" data-id="element-4591" data-mp="38exy1u5l4pmq">
                          {/* Static dashed base */}
                          <div className="absolute inset-0 border-l border-dashed border-white/25" data-id="element-4592" data-mp="ir6zctzmt0hi" />
                          {/* Glowing segment that fills top → bottom */}
                          <motion.div aria-hidden="true" className="absolute inset-x-0 top-0 origin-top" style={{
                        height: '100%'
                      }} initial={reducedMotion ? {
                        scaleY: 1
                      } : {
                        scaleY: 0
                      }} whileInView={{
                        scaleY: 1
                      }} viewport={{
                        once: true,
                        margin: '-100px'
                      }} transition={{
                        duration: reducedMotion ? 0 : 0.45,
                        delay: lightDelay + 0.1,
                        ease: 'easeInOut'
                      }} data-id="element-4593" data-mp="3u3bfm6k8z2cz">
                            <div className="absolute inset-0 w-[2px] -ml-px bg-[#EA580C] shadow-[0_0_12px_2px_rgba(234,88,12,0.6)]" data-id="element-4594" data-mp="3gcgbt8olvbg9" />
                          </motion.div>
                        </div>

                        {/* Title + body — centered. */}
                        <motion.div className="text-center max-w-[280px]" initial={reducedMotion ? {
                      opacity: 1,
                      y: 0
                    } : {
                      opacity: 0,
                      y: 15
                    }} whileInView={{
                      opacity: 1,
                      y: 0
                    }} viewport={{
                      once: true,
                      margin: '-100px'
                    }} transition={{
                      duration: 0.5,
                      delay: lightDelay + 0.15
                    }} data-id="element-4595" data-mp="136ryfpqsr3y5">
                          <h3 className="font-title font-medium text-xl md:text-2xl text-white mb-2.5 md:mb-3.5 tracking-tight leading-tight" data-id="element-4596" data-mp="1cvtaj4u8116u">
                            {step.title}
                          </h3>
                          <p className="text-sm md:text-base text-neutral-400 leading-relaxed" data-id="element-4597" data-mp="1gwg1ff9pjs3">
                            {step.body}
                          </p>
                        </motion.div>
                      </div>;
                })}
                </div>
              </div>}

            {/* Payoff — what "Giga gets it done" actually delivers */}
            {showSteps && <ResultNotification reducedMotion={!!reducedMotion} data-id="element-4598" data-mp="o5e97bi8widv" />}
          </div>
        </div>

        {/* CONNECTIONS Section */}
        {showConnections && <div className="relative z-10 w-full mt-16 md:mt-32" data-id="element-4599" data-mp="j960ogyjznfo">
            <div className="max-w-[1280px] mx-auto px-6 md:px-10 mb-10 md:mb-20" data-id="element-4600" data-mp="1fw28ei0czdow">
              <div className="text-center" data-id="element-4601" data-mp="1bvidll39cm5x">
                <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-4602" data-mp="1bxv3qlj662u2">
                  Connections
                </span>
                <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight leading-[1.1] text-white mb-5 md:mb-6" data-id="element-4603" data-mp="1255tftzcuv0b">
                  {copy.howItWorksConnections.headline2}
                </h2>
                <p className="text-base md:text-xl text-neutral-300 leading-relaxed max-w-3xl mx-auto" data-id="element-4604" data-mp="1ut1oku9a0euz">
                  Connect <span className="font-bold text-white" data-id="element-4605" data-mp="227lp37oagihs">ANY</span> tool
                  you use in a few clicks — most are live in seconds. Giga pulls
                  context between them so there's no more switching tabs,
                  copying data, or gluing information together yourself.
                </p>
              </div>
            </div>

            {/* Full-bleed marquee */}
            <div className="mb-10 md:mb-16" data-id="element-4606" data-mp="2l4d61agcylio">
              <LogoMarquee reducedMotion={!!reducedMotion} data-id="element-4607" data-mp="2q3g9wrklqlp2" />
            </div>

            {/* CTA */}
            <div className="max-w-[1280px] mx-auto px-6 md:px-10 flex justify-center" data-id="element-4608" data-mp="2utwqulnmspg6">
              <button onClick={() => navigate('/integrations')} className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-white hover:bg-neutral-50 text-[#2B211A] font-bold shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5" data-id="element-4609" data-mp="2zt9om1g1jgxj">
                See all connections
                <ArrowRight size={18} data-id="element-4610" data-mp="fdz2czq8se58" />
              </button>
            </div>
          </div>}
      </section>
    </PlatformCycleProvider>
  );
}